import { formatDate } from '@angular/common';
import { inject, Injectable, signal } from '@angular/core';
import { DateRange } from '@cumlaude/shared-components-inputs';
import { AIChatConversatieStatus, RAIChatConversatie } from '@cumlaude/service-contract';
import { RestService } from '@cumlaude/shared-services';
import { BehaviorSubject, combineLatest, switchMap, tap } from 'rxjs';

export enum ShowOption {
	AllChats = 'Alle chats',
	WithFeedback = 'Met feedback',
	OnlyPositive = 'Alleen positief',
	OnlyNegative = 'Alleen negatief',
}

/**
 * Houdt de filters van het chatoverzicht en het laatste zoekresultaat vast, zodat ze behouden blijven
 * na een bezoek aan een conversatie en de detailpagina kan doorbladeren door het zoekresultaat.
 */
@Injectable({ providedIn: 'root' })
export class AIChatsZoekService {
	private readonly restService = inject(RestService);

	readonly showOption$ = new BehaviorSubject<ShowOption>(ShowOption.AllChats);

	readonly gebruikerOfInstelling$ = new BehaviorSubject<string>('');

	readonly onderwerp$ = new BehaviorSubject<string>('');

	readonly dateRange$ = new BehaviorSubject<DateRange | undefined>(undefined);

	// Standaard de werkvoorraad: verwerkte chats staan uit de weg tot je ze expliciet erbij kiest.
	readonly status$ = new BehaviorSubject<AIChatConversatieStatus[]>([AIChatConversatieStatus.NIEUW, AIChatConversatieStatus.IN_BEHANDELING]);

	readonly resultaat = signal<RAIChatConversatie[] | undefined>(undefined);

	readonly aiChats$ = combineLatest([this.gebruikerOfInstelling$, this.onderwerp$, this.showOption$, this.dateRange$, this.status$]).pipe(
		switchMap(([gebruikerOfInstelling, onderwerp, showOption, dateRange, status]) => {
			let feedbackOptions: { heeftFeedback?: boolean; sentiment?: -1 | 1 } = {};
			switch (showOption) {
				case ShowOption.OnlyPositive:
					feedbackOptions = { heeftFeedback: true, sentiment: 1 };
					break;
				case ShowOption.OnlyNegative:
					feedbackOptions = { heeftFeedback: true, sentiment: -1 };
					break;
				case ShowOption.WithFeedback:
					feedbackOptions = { heeftFeedback: true };
					break;
			}
			return this.restService.getAIChatConversaties({
				gebruikerOfInstelling,
				onderwerp,
				status,
				gewijzigdVanaf: dateRange?.from ? formatDate(dateRange.from, 'yyyy-MM-dd', 'nl-NL') + ' 00:00:00.000' : undefined,
				gewijzigdTot: dateRange?.to ? formatDate(dateRange.to, 'yyyy-MM-dd', 'nl-NL') + ' 23:59:59.999' : undefined,
				...feedbackOptions,
			});
		}),
		tap((chats) => this.resultaat.set(chats))
	);
}
